import Immutable from 'immutable';
import { rulesCache, get } from 'ctm-util-functions';

const objValues = require('lodash.values');

class ValidationModel {

    /**
     Sets up a flat map of element id to element validation config
     @param {Object} formSchemaModel - The model for the form schema(s)
     */
    constructor(formSchemaModel) {
        this.formSchemaModel = formSchemaModel;
    }

    /**
     Creates a mapping of each page and it's elements to a simple validation
     model. This is used to set the initial state of the Validation reducer.
     @param {String} formStateKey - the stateKey to get the validation state model for
     @returns {Object} - write a description
     */
    stateModel = (formStateKey) => {
        const model = { touches: {}, pages: {}, invalidElements: [], outsideForm: {} };

        for (const page of this.formSchemaModel.pages[formStateKey]) {
            model.pages[page.name] = page.elements ? !page.elements.length > 0 : true;
        }
        for (const element of this.formSchemaModel.allElements[formStateKey]) {
            if (element.elType === 'fieldSet') {
                continue;
            }
            model.touches[element.identifier] = false;
        }
        return Immutable.fromJS(model);
    };

    /**
     * Get the error message for the specified validationRule
     * @param  {Object} validationRule - the failed validation rule for to get the error for.
     * @returns {String} - error message.
     */
    getErrorMessage = (validationRule) => {
        let message = '';
        const elementRule = rulesCache.getElementRuleForId(validationRule.id);
        if (elementRule.props.showValidationMessage === false) {
            // change to 'hideValidationMessage = true in schema
            return message;
        }

        const rulesResults = rulesCache.getRulesResultsForId(validationRule.id);
        const stateValues = rulesCache.getStateValuesForId(validationRule.id);
        // there may be multiple failing validation rules, collect their messages and then decide which one to show
        const possibleErrorMessages = {};
        for (const rule of Object.keys(rulesResults)) {
            if (elementRule.props.optionalIfValid) {
                // show an isSupplied like error message for rules with a failing optionalIfValid rule, if the value for the
                // current rule is not supplied, otherwise, show the error message for the failing rule
                const dependentRule = rulesCache.getRuleResult(elementRule.props.optionalIfValid, 'validation');
                if (!dependentRule.result && stateValues[elementRule.props.stateKey] === null) {
                    message = elementRule.props.errorMessages.optionalIfValid;
                    break;
                }
            }
            if (!rulesResults[rule] && elementRule.props.errorMessages) {
                possibleErrorMessages[rule] = elementRule.props.errorMessages[rule] || '';
            }
        }
        // If there are multiple failing validation rules and the elementRule defines errorMessagePrecedence
        // find the message for the first failing rule according to the errorMessagePrecedence and returns it's errorMessage
        if (elementRule.props.errorMessagePrecedence && Object.keys(possibleErrorMessages).length > 1) {
            for (const rule of elementRule.props.errorMessagePrecedence) {
                if (possibleErrorMessages[rule]) {
                    return possibleErrorMessages[rule];
                }
            }
        }
        // if errorMessagePrecedence is not defined, just return the errorMessage for the first failed rule
        return objValues(possibleErrorMessages)[0];
    };

    /**
     * Determine if the element that corresponds to elementName is currently visible
     * Check the visibility for the element, if not listed, check the visibility of the fieldSet that contains the element.
     * @param  {Map}     visibility  The Immutable Map that contains the element visibility values
     * @param  {String}  elementName The name of the form element to check visibility for
     * @returns {Boolean}             True if the element is currently visible
     */
    isElementVisible = (visibility, elementName) => {
        const elementVisibility = visibility.get(elementName);
        if (elementVisibility === undefined) {
            const fieldSet = this.formSchemaModel.getResolvedFieldSetForElement(elementName);
            return visibility.get(fieldSet.identifier);
        }

        return elementVisibility;
    };

    /**
     * Return the validation rule and update the touches for a given elementName
     * @param {String} elementID - The element ID to get the validation rule for
     * @param  {Object} rules    - The validation rules for the elements on the page
     * @param  {Map} visibility  - The Immutable Map that contains the element visibility values
     * @param {Object} touches   - The touches object
     * @returns {Object|null} - The validation rule if it exists, null otherwise
     */
    getElementValidationRule = (elementID, rules, visibility, touches) => {
        if (rules[elementID] === undefined || rules[elementID].validation === undefined) {
            return { result: true };
        }
        if (!this.isElementVisible(visibility, elementID)) {
            // @todo: might not need this, need to reset field set visibility on state
            // eslint-disable-next-line no-param-reassign
            touches[elementID] = false;
            return null;
        }
        // eslint-disable-next-line no-param-reassign
        touches[elementID] = true;

        let validationRule = rules[elementID].validation;
        const elementRule = rulesCache.getElementRuleForId(validationRule.id);
        // Handle rules which are optional, but where being optional depends on another field being in a valid
        // state. eg enter a mobile OR a landline number.
        if (get(elementRule, 'props.optionalIfValid') && !validationRule.result) {
            // If this rule is invalid, get the dependent rule and set it as the current validation rule
            validationRule = rules[elementRule.props.optionalIfValid].validation;
            if (validationRule.result) {
                // if the dependent rule is valid, reset the touch on this element so that no validation msg
                // is displayed
                // eslint-disable-next-line no-param-reassign
                touches[elementID] = false;
            }
        }
        return validationRule;
    };

    /**
     * Determine if the page that corresponds to pageName is valid, according to the
     * validation rules of the elements it contains
     * @param  {String} pageName   The name of the page to validate
     * @param  {Object} rules      The validation rules for the elements on the page
     * @param  {Map} visibility    The Immutable Map that contains the element visibility values
     * @returns {{touches: object, isValid: boolean}} The new set of touches for the page and the boolean validaity of the page
     */
    validatePage = (pageName, rules, visibility) => {
        // @todo: clean this up
        const pageElements = this.formSchemaModel.getPageElements(pageName);

        let isValid = true;

        const touches = {};
        const invalidElements = [];

        for (const element of pageElements) {
            const name = element.identifier;
            const validationRule = this.getElementValidationRule(name, rules, visibility, touches);
            if (!validationRule) {
                continue;
            }

            isValid = isValid && validationRule.result;

            if (!validationRule.result) {
                const elementRule = rulesCache.getElementRuleForId(validationRule.id);
                const rulesResults = rulesCache.getRulesResultsForId(validationRule.id);
                // filter and keep only rules that didn't validate then format them in JSON objects with their
                // matching validation errors
                const invalidRules = Object.keys(rulesResults).filter((rule) => {
                    return !rulesResults[rule];
                }).map((rule) => {
                    const elementRuleProps = elementRule.props;
                    const validationMessage = elementRuleProps.errorMessages && elementRuleProps.errorMessages[rule];
                    return {
                        name: rule,
                        validationMessage
                    };
                });

                invalidElements.push({ name, invalidRules });
            }
        }
        return { touches, isValid, invalidElements };
    };

}

export { ValidationModel };
